/**
 * One client — `docs/rowted-admin-v3.md` §2.2, board 2.
 *
 * `<RecordPage>` with `object: 'client'`. The same shell a contact and a job
 * use, which is what `AnyRecordType` was for: the header, the tab row, the
 * `wide` rule that stands the rail aside for a list tab, the pills from
 * `GET /platform/records/client/:id/summary`, and the phone layout.
 *
 * **Three of the four tabs are the list engine** (§2.3–2.4): People is
 * `client-people`, Activity is `client-activity` — a fourth mount of the same
 * area an admin's own Activity tab uses — and Support sessions is
 * `client-sessions`, with the tray that shows what was changed inside a visit.
 * Each is its own mount, so each has its own saved views.
 *
 * **`SignInAs` is unchanged and is the point of the page.** §6.12 wants a
 * reason, a consent flag and a time limit recorded before an employee can open
 * somebody else's account, and every action taken inside it carries
 * `impersonator_user_id` so the customer's own History tab says who really
 * did it. It is the record's primary action now rather than a button on
 * eighty-one rows.
 *
 * **What §2.2 asks for and is not here**, because the data does not exist
 * rather than because it was skipped: "What this client connected" (every
 * integration credential today is platform-wide, so a per-tenant QuickBooks
 * or Stripe Connect row is not a thing to read), "failed jobs 24 h"
 * (`pgboss.job` rows carry no tenant), and a Files tab (the platform has no
 * bucket for client documents). Each is written down in
 * `services/platform-usage.ts` beside the query that would have read it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ApiError, SELLABLE_FEATURES, type FeatureDef,
} from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import {
  Avatar, Button, EmptyState, Switch, humanize,
} from '../../components/primitives.js'
import { Modal, Toast } from '../../components/overlays.js'
import { Field, TextAreaField } from '../../components/fields.js'
import { RecordPage, type RecordConfig } from '../../record/record-page.js'
import { RecordCard } from '../../record/record-rail.js'
import { RelatedList } from '../../components/related-list.js'
import { useRecordSummary } from '../../lib/use-record.js'
import {
  EM_DASH, formatDocumentDate, formatNumber, formatRelative,
} from '../../lib/format.js'
import { IconClock, IconExternal, IconPin, NAV_ICONS } from '../../frame/icons.js'
import { AdminFact, AdminRows, AdminStat } from './panel.js'
import { AdminPage, Loading, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'
import { InviteLink, type Delivered } from './invite-link.js'
import type { MenuItem } from '../../components/context-menu.js'
import type { ListRow } from '../../lib/use-list.js'

/**
 * What `GET /platform/clients/:id` answers with.
 *
 * Its own shape, not the list's. The list is an `AreaDef`
 * (`services/platform-areas.ts`) whose columns are whatever somebody has
 * chosen to show; a detail page asks for the same facts every time.
 */
interface ClientDetail {
  tenant: {
    id: string; name: string; slug: string
    kind: 'production' | 'trial' | 'demo'
    status: string; createdAt: string
    trialEndsAt: string | null; tier: string | null
    subscriptionStatus: string | null
    stripeSubscriptionId: string | null
    timezone: string; currency: string; currentPeriodEnd: string | null
  }
  users: {
    id: string; name: string; email: string; status: string
    lastSignInAt: string | null; roles: string
    /** Ever set a password — `status` cannot say (an owner is active from signup). */
    passwordSet: boolean
  }[]
  /** The last thirty days, not everything ever — v3 §2.2. */
  usage: {
    activeUsers: number; jobsCreated: number; visitsCompleted: number
    invoicesSent: number; storageBytes: number
  }
  health: {
    state: 'healthy' | 'card_needed' | 'quiet' | 'demo' | 'read_only'
    cardOnFile: boolean
    lastSignInAt: string | null
    deliveryProblems: number
    seats: number
  }
  /**
   * What this company has been sold — one row per decided key, not a list of
   * the ones that are on: `enabled: false` is Rowted having turned something
   * off, and a key with no row at all has never been decided either way.
   * `source` is what `set_source` says (migration 0110).
   */
  features: {
    key: string; enabled: boolean
    source: 'user' | 'onboarding' | 'migration' | 'admin'
    reason: string | null; updatedAt: string
  }[]
  notes: { id: string; body: string; createdAt: string; author: string | null }[]
  impersonations: {
    id: string; reason: string; readOnly: boolean; startedAt: string
    expiresAt: string; endedAt: string | null; endedReason: string | null
    platformUser: string; targetUser: string | null
  }[]
}

type Person = ClientDetail['users'][number]

/** The two links a client's person can be sent from here (step 27). */
type LinkKind = 'invite' | 'password_reset'

/** How long each lasts, said the way the email says it (`TOKEN_TTL_MINUTES`). */
const LASTS: Record<LinkKind, string> = { invite: '7 days', password_reset: '15 minutes' }

/**
 * Whether somebody still needs their invite rather than a reset.
 *
 * Asked of the password, not the status: an owner made by signup — and so by
 * New client — is `active` from the first moment, with an invite in their
 * inbox they may never have opened. The server decides the same way.
 */
export const needsInvite = (person: Pick<Person, 'status' | 'passwordSet'>): boolean =>
  person.status !== 'suspended' && (person.status === 'invited' || !person.passwordSet)

/**
 * What a person's ⋮ offers, on the People tab and nowhere else.
 *
 * Two links for two different people: somebody who never set a password gets
 * the invite again, somebody who did gets a reset — and the server refuses
 * each for the other, so offering both would be offering one button that is
 * always wrong. A suspended person gets neither: their company suspended
 * them, and a link from Rowted is not the way back in.
 */
export function personMenu(
  row: Pick<Person, 'id' | 'email' | 'status' | 'passwordSet'>,
  can: { write: boolean; signIn: boolean },
  act: { send: (kind: LinkKind) => void; signInAs: () => void },
): MenuItem[] {
  const items: MenuItem[] = []
  const invite = needsInvite(row)
  if (can.write && invite) {
    items.push({ key: 'invite', label: 'Resend invite', onSelect: () => act.send('invite') })
  }
  if (can.write && !invite && row.status === 'active') {
    items.push({
      key: 'reset', label: 'Send a password reset', onSelect: () => act.send('password_reset'),
    })
  }
  if (can.signIn && row.status === 'active') {
    items.push({ key: 'sign-in-as', label: 'Sign in as…', onSelect: act.signInAs })
  }
  return items
}

/** The shape the config closes over: the payload, plus what only this page does. */
interface ClientRecordData extends ClientDetail {
  maySignIn: boolean
  mayWrite: boolean
  onSignInAs: (user: Person) => void
  /** An invite again, or a password reset — see `sendLink`. */
  onSendLink: (user: Pick<Person, 'id' | 'email'>, kind: LinkKind) => void
  onNote: () => void
  onFeatures: () => void
  onRemove: () => void
  onOpenTab: (tab: string) => void
}

/**
 * A header tag's colour, from the tokens rather than from a hex.
 *
 * `--status-job-*` because those are the pairs §3.1 already defines and the
 * design-token test insists nothing hand-types one — the same reasoning the
 * admin record's role tags carry.
 */
const pair = (name: string) => ({
  fg: `rgb(var(--status-job-${name}-fg))`,
  bg: `rgb(var(--status-job-${name}-bg))`,
})

const KIND_TONE: Record<ClientDetail['tenant']['kind'], { fg: string; bg: string }> = {
  production: pair('completed'),
  trial: pair('scheduled'),
  demo: pair('requested'),
}

/** The same five words the clients list prints, from the same CASE server-side. */
const HEALTH_WORDS: Record<
  ClientDetail['health']['state'], { label: string; tone: { fg: string; bg: string } }
> = {
  healthy: { label: 'Healthy', tone: pair('completed') },
  card_needed: { label: 'Card needed', tone: pair('returned') },
  quiet: { label: 'Quiet', tone: pair('requested') },
  demo: { label: 'Demo', tone: pair('requested') },
  read_only: { label: 'Read only', tone: pair('requested') },
}

/** `1.4 GB`, `812 MB`, `0 bytes` — a usage tile has room for three characters. */
function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0'
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB']
  const step = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** step
  return `${value >= 10 || step === 0 ? Math.round(value) : value.toFixed(1)} ${units[step]}`
}

// ---------------------------------------------------------------------------

export function AdminClient() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { me, ready } = useAdminUser()
  const [data, setData] = useState<ClientDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signingInAs, setSigningInAs] = useState<Person | null>(null)
  const [picking, setPicking] = useState(false)
  const [params, setParams] = useSearchParams()
  const [noting, setNoting] = useState(false)
  const [features, setFeatures] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  /* A link the email did not carry, to hand over by hand — see `InviteLink`. */
  const [undelivered, setUndelivered] = useState<
    (Delivered & { link: string; kind: LinkKind }) | null
  >(null)
  /*
   * One dialog for every code this page has to ask for. `SignInAs` still
   * asks inline, because its code box sits inside a form somebody is filling
   * in; a switch panel has nothing to hold, so `guard()` replaying the save
   * is the whole interaction.
   */
  const stepUp = useStepUp()

  const { summary } = useRecordSummary('client', id || undefined, { request: adminRequest })

  const load = useCallback(async () => {
    try {
      setData(await adminRequest<ClientDetail>(`/clients/${id}`))
      setError(null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not load.')
    }
  }, [id])

  useEffect(() => { if (ready && me) void load() }, [ready, me, load])

  /*
   * `?signInAs=1` opens the picker — the clients list's row menu and the
   * *Create and sign in as owner* button both arrive that way. The flag is
   * taken out of the URL once it has been read, so a refresh does not reopen
   * a dialog somebody closed.
   */
  useEffect(() => {
    if (params.get('signInAs') !== '1') return
    setPicking(true)
    const next = new URLSearchParams(params)
    next.delete('signInAs')
    setParams(next, { replace: true })
  }, [params, setParams])

  const maySignIn = can(me, 'signInAs')
  const mayWrite = can(me, 'clients')

  const openTab = useCallback((tab: string) => {
    navigate(`/admin/clients/${id}?tab=${tab}`, { replace: true })
  }, [id, navigate])

  /**
   * An invite again, or a password reset (Aaron, step 27: "I can't resend the
   * invite to the owner").
   *
   * Behind the console's step-up, like **New client**: a set-password link is
   * a live credential for somebody else's account. The answer decides what
   * is said — sent, or the link to hand over because the email did not go —
   * and it is read inside the guarded action so a replay after a code reports
   * itself exactly like a first attempt.
   */
  const { guard } = stepUp
  const sendLink = useCallback((user: Pick<Person, 'id' | 'email'>, kind: LinkKind) => {
    setNotice(null)
    setProblem(null)
    const path = kind === 'invite' ? 'resend-invite' : 'password-reset'
    void guard(async () => {
      const answer = await adminRequest<Delivered>(
        `/clients/${id}/people/${user.id}/${path}`, { method: 'POST', body: {} })
      if (answer.delivered) {
        setNotice(`${kind === 'invite' ? 'Invite' : 'Password reset'} sent to `
          + `${answer.email ?? user.email} · expires in ${LASTS[kind]}.`)
      } else if (answer.link) {
        setUndelivered({ ...answer, email: answer.email ?? user.email, link: answer.link, kind })
      } else {
        setProblem(answer.reason ?? 'The link was made, but the email did not send.')
      }
    }).catch((caught: unknown) => {
      setProblem(caught instanceof ApiError ? caught.message : 'That did not work.')
    })
  }, [id, guard])

  const record = useMemo<ClientRecordData | null>(() => (data ? {
    ...data,
    maySignIn,
    mayWrite,
    onSignInAs: (user: Person) => setSigningInAs(user),
    onSendLink: sendLink,
    onNote: () => setNoting(true),
    onFeatures: () => setFeatures(true),
    onRemove: () => setRemoving(true),
    onOpenTab: openTab,
  } : null), [data, maySignIn, mayWrite, openTab, sendLink])

  const config = useMemo(() => clientConfig(() => setPicking(true)), [])

  if (!ready || (!record && !error)) return <AdminPage me={me}><Loading /></AdminPage>
  if (error || !record) {
    return (
      <AdminPage me={me}>
        <EmptyState title="Not available" body={error ?? 'That client could not be loaded.'} />
      </AdminPage>
    )
  }

  const base = [{ field: 'tenantId', op: 'eq' as const, value: record.tenant.id }]

  return (
    <AdminPage me={me}>
      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => (
          tab === 'overview'
            ? <ClientOverview record={record} />
            : tab === 'people'
              ? (
                <RelatedList
                  area="client-people"
                  title="People"
                  variant="tab"
                  request={adminRequest}
                  listKey="people@client"
                  baseFilter={base}
                  fields={['name', 'roles', 'homeBranch', 'status', 'mfa', 'lastSignInAt']}
                  rowMenu={(row: ListRow) => {
                    const person = {
                      id: String(row.id),
                      email: String(row.email ?? ''),
                      status: String(row.status ?? ''),
                      passwordSet: row.passwordSet === true,
                    }
                    return personMenu(
                      person,
                      { write: record.mayWrite, signIn: record.maySignIn },
                      {
                        send: (kind) => record.onSendLink(person, kind),
                        signInAs: () => {
                          const user = record.users.find((one) => one.id === person.id)
                          if (user) record.onSignInAs(user)
                        },
                      })
                  }}
                  emptyTitle="Nobody has an account yet."
                  emptyBody="A signup or an invite makes the first one."
                />
              )
              : tab === 'activity'
                ? (
                  <RelatedList
                    area="client-activity"
                    title="Activity"
                    variant="tab"
                    request={adminRequest}
                    listKey="activity@client"
                    baseFilter={base}
                    /*
                      * Four columns, and each of them differs between rows.
                      * `source` reads "Audit log" on nearly every row once
                      * the list is filtered to one client, and `actionCount`
                      * is zero on everything but a visit — a column that says
                      * the same thing four hundred times carries no
                      * information. Both stay in the field catalogue, so
                      * somebody who wants one can add it, and the Σ row can
                      * still total the changes.
                      */
                    fields={['at', 'who', 'title', 'access']}
                    emptyTitle="Nothing recorded yet."
                    emptyBody="Every time somebody at Rowted reads or changes this client, it
                               lands here."
                  />
                )
                : (
                  <RelatedList
                    area="client-sessions"
                    title="Support sessions"
                    variant="tab"
                    request={adminRequest}
                    listKey="sessions@client"
                    baseFilter={base}
                    fields={['startedAt', 'who', 'reason', 'access', 'minutes', 'actionCount']}
                    emptyTitle="Nobody from Rowted has signed in as this client."
                    emptyBody="Every visit is listed here, with what was changed inside it."
                  />
                )
        )}
      </RecordPage>

      {picking && (
        <PickPerson
          people={record.users}
          onClose={() => setPicking(false)}
          onPick={(user) => { setPicking(false); setSigningInAs(user) }}
        />
      )}
      {signingInAs && (
        <SignInAs
          tenantId={record.tenant.id}
          user={signingInAs}
          onClose={() => setSigningInAs(null)}
        />
      )}
      {noting && (
        <NoteDialog
          company={record.tenant.name}
          onClose={() => setNoting(false)}
          onSave={async (body) => {
            await adminRequest(`/clients/${record.tenant.id}/notes`, { method: 'POST', body: { body } })
            setNoting(false)
            setNotice('Noted.')
            await load()
          }}
        />
      )}
      {features && (
        <FeaturesDialog
          company={record.tenant.name}
          tier={record.tenant.tier}
          rows={record.features}
          onClose={() => setFeatures(false)}
          onSave={async (next, reason) => {
            /*
             * `guard` runs the save, and if the console session has gone cold
             * it asks for a code and runs it again — so the dialog closes on
             * the save that actually happened rather than on the one that was
             * refused. `ran` is false only when somebody shut the code
             * dialog, and then nothing has changed and nothing is said.
             */
            const ran = await stepUp.guard(async () => {
              await adminRequest(`/clients/${record.tenant.id}/features`, {
                method: 'PUT',
                body: { features: next, ...(reason ? { reason } : {}) },
              })
            })
            if (!ran) return
            setFeatures(false)
            setNotice('Saved. Their app picks it up within half a minute.')
            await load()
          }}
        />
      )}
      {removing && (
        <RemoveClient
          name={record.tenant.name}
          kind={record.tenant.kind}
          people={record.users.length}
          onClose={() => setRemoving(false)}
          onRemove={async (confirmName, reason) => {
            /*
             * Behind the step-up like every other change on this page, and
             * the answer read inside the guarded action so a replay after a
             * code ends in the same place a first attempt does: the list,
             * saying what went.
             */
            const ran = await stepUp.guard(async () => {
              await adminRequest(`/clients/${record.tenant.id}/remove`, {
                method: 'POST',
                body: { confirmName, ...(reason ? { reason } : {}) },
              })
            })
            if (!ran) return
            navigate('/admin/clients', {
              replace: true,
              state: { notice: `${record.tenant.name} was removed.` },
            })
          }}
        />
      )}
      {undelivered && (
        <InviteLink
          answer={undelivered}
          kind={undelivered.kind}
          lasts={LASTS[undelivered.kind]}
          onClose={() => setUndelivered(null)}
        />
      )}
      {stepUp.dialog}
      {notice && (
        <Toast tone="success" onDismiss={() => setNotice(null)}>{notice}</Toast>
      )}
      {problem && (
        <Toast tone="danger" onDismiss={() => setProblem(null)}>{problem}</Toast>
      )}
    </AdminPage>
  )
}

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

function clientConfig(openPicker: () => void): RecordConfig<ClientRecordData> {
  return {
    object: 'client',

    breadcrumb: (record) => [
      { label: 'Clients', href: '/admin/clients' },
      { label: record.tenant.name },
    ],

    mark: (record) => <Avatar name={record.tenant.name} size={56} tone="accent" />,

    title: (record) => record.tenant.name,

    /*
     * Kind, plan and health — §2.2. Three facts about the account that a
     * support person needs before reading anything else: what sort of
     * customer this is, what they are on, and whether anything is wrong.
     */
    tags: (record) => [
      { label: humanize(record.tenant.kind), tone: KIND_TONE[record.tenant.kind] },
      ...(record.tenant.tier
        ? [{ label: humanize(record.tenant.tier) }]
        : []),
      HEALTH_WORDS[record.health.state],
    ],

    meta: (record) => [
      {
        /*
         * The customer's own address, and a link to it. Not a route change —
         * this is a different app on a different host, and the point of
         * showing it is that somebody can go and look.
         */
        icon: <IconExternal size={14} />,
        text: `app.rowted.com/${record.tenant.slug}`,
        href: `/${record.tenant.slug}`,
      },
      { icon: <IconPin size={14} />, text: record.tenant.timezone },
      {
        icon: <IconClock size={14} />,
        text: `Client since ${formatDocumentDate(record.tenant.createdAt)}`,
      },
      ...(record.users[0]
        ? [{ icon: <NAV_ICONS.contacts size={14} />, text: `Owner · ${record.users[0].name}` }]
        : []),
    ],

    primary: (record) => (record.maySignIn
      ? { label: 'Sign in as…', icon: <NAV_ICONS.contacts size={15} />, onClick: openPicker }
      : null),

    /*
     * **Change plan, Suspend and Edit are not here yet.** §2.2 names all
     * three, and each needs an endpoint this branch does not have — a button
     * that opens a flyout with nowhere to save is worse than no button. The
     * two that do work are here.
     */
    actions: (record) => (record.mayWrite
      ? [{ label: 'Add a note', onClick: record.onNote }]
      : []),

    /*
     * **Remove test client** (Aaron, step 27), for a trial or a demo only —
     * and absent, not greyed, for a production client, because migration
     * 0131 refuses one whatever a screen offers and a disabled item is an
     * invitation to keep trying. The danger tone and the typed name are the
     * two things between a slip and a client nobody can bring back.
     */
    menu: (record) => (record.mayWrite && record.tenant.kind !== 'production'
      ? [{
        key: 'remove',
        label: 'Remove test client…',
        tone: 'danger' as const,
        onSelect: record.onRemove,
      }]
      : []),

    tabs: (record, summary) => [
      { key: 'overview', label: 'Overview', icon: 'home' },
      {
        key: 'people',
        label: 'People',
        icon: 'user',
        wide: true,
        count: summary?.counts['client-people'] ?? record.users.length,
      },
      {
        key: 'activity',
        label: 'Activity',
        icon: 'clock',
        wide: true,
        ...(summary ? { count: summary.counts['client-activity'] ?? 0 } : {}),
      },
      {
        key: 'sessions',
        label: 'Support sessions',
        icon: 'access',
        wide: true,
        count: summary?.counts['client-sessions'] ?? record.impersonations.length,
      },
    ],

    rail: (record) => (
      <>
        <OwnerCard record={record} />
        <HealthCard record={record} />
        <FeaturesCard record={record} />
        <NotesCard record={record} />
      </>
    ),

    phone: {
      quickActions: (record) => (record.maySignIn
        ? [{ label: 'Sign in as', icon: <NAV_ICONS.contacts size={16} />, onClick: openPicker }]
        : []),
      tabBar: ['overview', 'people', 'activity', 'sessions'],
    },
  }
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

/**
 * What this client has been doing, and what they pay — §2.2.
 *
 * Two cards rather than the plan's four: "What this client connected" has no
 * per-tenant source to read (every integration credential is platform-wide
 * today), and "Recent" would repeat the Activity tab one tab away.
 */
function ClientOverview({ record }: { record: ClientRecordData }) {
  const { tenant, usage } = record
  return (
    <div className="flex flex-col gap-3.5">
      <RecordCard title="Usage · last 30 days">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-[18px] pb-4 pt-3 sm:grid-cols-5">
          <AdminStat label="Signed in" value={formatNumber(usage.activeUsers)} sub="people" />
          <AdminStat label="Jobs made" value={formatNumber(usage.jobsCreated)} />
          {/*
            * "Visits completed", not "visits closed": a visit is a
            * `work_items` row and that table has no completion column. The
            * label says what the SQL counted.
            */}
          <AdminStat label="Visits done" value={formatNumber(usage.visitsCompleted)} />
          <AdminStat label="Invoices sent" value={formatNumber(usage.invoicesSent)} />
          <AdminStat label="Files" value={formatBytes(usage.storageBytes)} />
        </div>
      </RecordCard>

      <RecordCard title="Subscription">
        <AdminRows>
          <AdminFact label="Plan" value={tenant.tier ? humanize(tenant.tier) : 'No plan'} />
          {/*
            * `trialing` is not an attention item on a trial — it is what a
            * trial *is*. The flag was `!== 'active'`, so every client made
            * yesterday opened its record wearing "Needs attention" on the day
            * it was created, which teaches somebody to ignore the word.
            */}
          <AdminFact
            label="Subscription"
            value={tenant.subscriptionStatus ? humanize(tenant.subscriptionStatus) : 'None'}
            {...(tenant.subscriptionStatus
              && !['active', 'trialing'].includes(tenant.subscriptionStatus)
              ? { tag: 'Needs attention', tone: 'warn' as const }
              : {})}
          />
          <AdminFact
            label="Period ends"
            value={tenant.currentPeriodEnd
              ? formatDocumentDate(tenant.currentPeriodEnd)
              : EM_DASH}
          />
          {/*
            * Only on a trial. A trial end date on a production client is a
            * date that means nothing, and a row that means nothing is a row
            * somebody reads twice before ignoring.
            */}
          {tenant.kind === 'trial' && (
            <AdminFact
              label="Trial ends"
              value={tenant.trialEndsAt ? formatDocumentDate(tenant.trialEndsAt) : 'No end set'}
              {...(tenant.trialEndsAt && Date.parse(tenant.trialEndsAt) < Date.now()
                ? { tag: 'Over', tone: 'bad' as const }
                : {})}
            />
          )}
          <AdminFact label="Currency" value={tenant.currency} />
          <AdminFact
            label="In Stripe"
            value={tenant.stripeSubscriptionId ?? 'Not connected'}
            {...(tenant.stripeSubscriptionId ? {} : { tag: 'No card', tone: 'warn' as const })}
          />
        </AdminRows>
      </RecordCard>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

/**
 * Who to talk to, and a way in as each of them.
 *
 * Five rows, because the full list is the People tab and this is a peek.
 * The owner first — `GET /clients/:id` orders by `created_at`, which is who
 * the account was opened for.
 */
function OwnerCard({ record }: { record: ClientRecordData }) {
  const few = record.users.slice(0, 5)
  return (
    <RecordCard
      title="Owner and contacts"
      count={record.users.length}
      {...(record.users.length > 5
        ? { action: { label: 'People ›', onClick: () => record.onOpenTab('people') } }
        : {})}
    >
      <div className="px-[18px] pb-3 pt-1">
        {few.length === 0
          ? <p className="m-0 py-1.5 text-cell text-text-muted">Nobody yet.</p>
          : few.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-2.5 border-b border-line-soft py-1.5
                         text-cell last:border-b-0"
            >
              <Avatar name={user.name} size={24} />
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate">{user.name}</span>
                <span className="truncate text-[11.5px] text-text-faint">
                  {user.roles || 'no role'}
                </span>
              </span>
              {/*
                * Absent rather than disabled for a role that may not, the
                * same rule the Settings page's Change follows: a greyed
                * button on every row is a screen inviting somebody to keep
                * trying.
                */}
              {record.maySignIn && user.status === 'active' && (
                <button
                  type="button"
                  onClick={() => record.onSignInAs(user)}
                  className="shrink-0 text-[11.5px] font-semibold text-accent
                             hover:text-accent-dark"
                >
                  Sign in as
                </button>
              )}
              {/*
                * The owner of a client an admin just made has never set a
                * password until they open the email — and the one thing worth
                * doing then is sending it again (Aaron, step 27).
                */}
              {record.mayWrite && needsInvite(user) && (
                <button
                  type="button"
                  onClick={() => record.onSendLink(user, 'invite')}
                  className="shrink-0 text-[11.5px] font-semibold text-accent
                             hover:text-accent-dark"
                >
                  Resend invite
                </button>
              )}
            </div>
          ))}
      </div>
    </RecordCard>
  )
}

/** Is anything wrong — §2.2, and the same word the list's chip prints. */
function HealthCard({ record }: { record: ClientRecordData }) {
  const { health } = record
  return (
    <RecordCard title="Health">
      <AdminRows>
        <AdminFact
          label="Card on file"
          value={health.cardOnFile ? 'Yes' : 'No'}
          {...(health.cardOnFile ? {} : { tag: 'Needed', tone: 'warn' as const })}
        />
        <AdminFact
          label="Last sign-in"
          value={health.lastSignInAt ? formatRelative(health.lastSignInAt) : 'Nobody yet'}
        />
        <AdminFact label="Seats" value={formatNumber(health.seats)} />
        {/*
          * Failures, and the label says failures. Rowted keeps no log of mail
          * *sent*, so a "97% delivered" would be a statistic about nothing —
          * `delivery_exceptions` is a row per bounce and that is the number.
          */}
        <AdminFact
          label="Delivery problems"
          value={formatNumber(health.deliveryProblems)}
          {...(health.deliveryProblems > 0 ? { tone: 'bad' as const, tag: '30 d' } : {})}
        />
      </AdminRows>
    </RecordCard>
  )
}

/**
 * What Rowted's own staff know about this customer — §2.2, migration 0090.
 *
 * Not `activities` inside the tenant: the customer never sees these, and a
 * note that could leak into their own History tab is a note nobody would
 * write honestly.
 */
/**
 * What this company is sold — the card, and the only door to the switches.
 *
 * `tenant_features` had one writer until migration 0110: `signUp()`, copying
 * the tier's list at the moment the account was made. So a company that
 * signed up before a feature existed could never be given it, and the three
 * default tiers name no features at all, which means every real company has
 * every sellable feature **off**. Property maps is the first feature anything
 * gates on, so it was the first place that showed: a Map tab that could not
 * appear on any property of any real company.
 *
 * **On Rowted's side of the house, not the company's own settings.** A
 * feature is what Rowted sells — `shared/src/features.ts` draws the line
 * between that, what a *person* may do (the permission matrix) and how a
 * company has configured something it already has (its settings). A company
 * cannot tick its own invoice.
 *
 * The card says which are on and names the rest, rather than drawing twelve
 * rows: the question somebody opens a client's record with is "what do they
 * have", and the full list belongs in the dialog that changes it. **Where a
 * switch came from is on the row** — a feature the tier gave them and one
 * Rowted turned on afterwards are different facts, and `set_source` is the
 * column that keeps them apart.
 */
function FeaturesCard({ record }: { record: ClientRecordData }) {
  const decided = new Map(record.features.map((row) => [row.key, row]))
  const on = SELLABLE_FEATURES.filter((one) => decided.get(one.key)?.enabled)
  const off = SELLABLE_FEATURES.filter((one) => !decided.get(one.key)?.enabled)

  return (
    <RecordCard
      title="What they're sold"
      {...(record.mayWrite
        ? { action: { label: 'Edit', onClick: record.onFeatures } }
        : {})}
    >
      <div className="flex flex-col gap-2.5 px-[18px] pb-4 pt-2">
        {on.length === 0
          ? (
            <p className="m-0 text-cell text-text-muted">
              Core only — quoting, scheduling and invoicing. Everything else is off,
              so the screens it would add are not drawn.
            </p>
          )
          : on.map((one) => {
            const row = decided.get(one.key)
            return (
              <div key={one.key} className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 text-cell text-text">{one.label}</span>
                <span className="shrink-0 text-[11px] text-text-faint">
                  {row?.source === 'onboarding' ? 'with the plan' : 'set by Rowted'}
                </span>
              </div>
            )
          })}

        {on.length > 0 && off.length > 0 && (
          <p className="m-0 text-[11px] leading-[1.45] text-text-faint">
            Off: {off.map((one) => one.label).join(' · ')}
          </p>
        )}
      </div>
    </RecordCard>
  )
}

function NotesCard({ record }: { record: ClientRecordData }) {
  return (
    <RecordCard
      title="Rowted notes"
      {...(record.mayWrite
        ? { action: { label: '+ Add', onClick: record.onNote } }
        : {})}
    >
      <div className="flex flex-col gap-2.5 px-[18px] pb-4 pt-2">
        {record.notes.length === 0
          ? (
            <p className="m-0 text-cell text-text-muted">
              Nothing written down. The customer never sees these.
            </p>
          )
          : record.notes.slice(0, 6).map((note) => (
            <div key={note.id} className="flex flex-col gap-0.5">
              <span className="text-cell leading-[1.45] text-text">{note.body}</span>
              <span className="text-[11px] text-text-faint">
                {note.author ?? 'somebody since removed'} · {formatRelative(note.createdAt)}
              </span>
            </div>
          ))}
      </div>
    </RecordCard>
  )
}

// ---------------------------------------------------------------------------
// The three dialogs
// ---------------------------------------------------------------------------

/**
 * Which of their people to be — the step before `SignInAs`.
 *
 * It used to be a Sign in as button on all eighty-one rows of a card. One
 * primary action and a picker is the record idiom, and it also means the
 * decision "who am I going in as" is made on purpose rather than by which row
 * happened to be under the cursor.
 */
function PickPerson({ people, onClose, onPick }: {
  people: Person[]
  onClose: () => void
  onPick: (user: Person) => void
}) {
  const [search, setSearch] = useState('')
  const active = people.filter((user) => user.status === 'active')
  const needle = search.trim().toLowerCase()
  const shown = needle
    ? active.filter((user) => `${user.name} ${user.email} ${user.roles}`
      .toLowerCase().includes(needle))
    : active

  return (
    <Modal open title="Sign in as whom?" onClose={onClose} width={460}>
      <div className="flex flex-col gap-3 p-4">
        <Field label="Search" value={search} onChange={setSearch} autoFocus />
        <div className="max-h-[320px] overflow-y-auto">
          {shown.length === 0
            ? <p className="m-0 py-2 text-cell text-text-muted">Nobody matches.</p>
            : shown.slice(0, 50).map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => onPick(user)}
                className="flex w-full items-center gap-2.5 border-b border-line-soft py-2
                           text-left text-cell last:border-b-0 hover:bg-hover"
              >
                <Avatar name={user.name} size={26} />
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-[11.5px] text-text-faint">
                    {user.email} · {user.roles || 'no role'}
                  </span>
                </span>
              </button>
            ))}
        </div>
        {shown.length > 50 && (
          <p className="m-0 text-[11.5px] text-text-faint">
            Showing 50 of {shown.length}. Search for the rest.
          </p>
        )}
      </div>
    </Modal>
  )
}

/** A note on the customer, for Rowted's own eyes — migration 0090. */
function NoteDialog({ company, onClose, onSave }: {
  company: string
  onClose: () => void
  onSave: (body: string) => Promise<void>
}) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      title={`A note about ${company}`}
      subtitle="Only Rowted sees this. It is not in their own history."
      onClose={onClose}
      width={460}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || body.trim() === ''}
            onClick={() => {
              setBusy(true)
              setError(null)
              onSave(body.trim()).catch((cause: unknown) => {
                setError(cause instanceof ApiError ? cause.message : 'That did not save.')
                setBusy(false)
              })
            }}
          >
            {busy ? 'Saving…' : 'Save note'}
          </Button>
        </>
      )}
    >
      <div className="p-4">
        <TextAreaField label="Note" value={body} onChange={setBody} rows={4} />
        {error && <p role="alert" className="m-0 pt-2 text-cell text-danger">{error}</p>}
      </div>
    </Modal>
  )
}

/**
 * The switches — the door `tenant_features` did not have.
 *
 * **Every sellable key is sent with a boolean**, not a list of the ticked
 * ones. A payload of ticks cannot tell "Rowted turned this off" from "nobody
 * has ever decided", and `set_source` exists to keep those apart; sending
 * both halves is what lets the row say which. Core features are not offered
 * at all — `featureOn()` answers true for them whatever a row says, so a
 * switch for one would be a control that does nothing.
 *
 * **`needs` is a warning, never a block.** `property_maps` needs the Map
 * Tiles key and `ai` needs Azure: switching one on without its key gives a
 * company screens whose data will not load, which is worth saying on the row
 * — but the key is Rowted's to set in Integrations and may well be set an
 * hour later, so refusing the switch would be the wrong order of operations.
 *
 * The reason is optional and lands on the audit row beside the keys that
 * changed. It is not demanded the way `SignInAs` demands one: reading
 * somebody's data needs a justification a person can be held to, and selling
 * them a feature is an ordinary commercial act.
 */
function FeaturesDialog({ company, tier, rows, onClose, onSave }: {
  company: string
  tier: string | null
  rows: ClientRecordData['features']
  onClose: () => void
  onSave: (features: Record<string, boolean>, reason: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<Record<string, boolean>>(() => {
    const decided = new Map(rows.map((row) => [row.key, row.enabled]))
    return Object.fromEntries(
      SELLABLE_FEATURES.map((one) => [one.key, decided.get(one.key) ?? false]))
  })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
   * Nothing to save is the common case — somebody opened the panel to read
   * it. The button is disabled rather than absent, because a greyed Save says
   * "there is nothing different here" and a missing one says "this panel
   * cannot save".
   */
  const decided = new Map(rows.map((row) => [row.key, row.enabled]))
  const changed = SELLABLE_FEATURES
    .filter((one) => draft[one.key] !== (decided.get(one.key) ?? false))
    .length

  return (
    <Modal
      open
      /*
       * The card's own words, and the company in the subtitle rather than in
       * the title: `Modal` lays the two out side by side, so a title long
       * enough to wrap pushes the subtitle into an ellipsis and the header
       * says neither thing properly. What is in every plan is a sentence in
       * the body, where there is a line's width for it.
       */
      title="What they're sold"
      subtitle={tier ? `${company} · ${tier} plan` : company}
      onClose={onClose}
      width={520}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || changed === 0}
            onClick={() => {
              setBusy(true)
              setError(null)
              onSave(draft, reason.trim()).catch((cause: unknown) => {
                setError(cause instanceof ApiError ? cause.message : 'That did not save.')
                setBusy(false)
              })
            }}
          >
            {busy
              ? 'Saving…'
              : changed === 0 ? 'Save' : `Save ${changed} change${changed === 1 ? '' : 's'}`}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-cell text-text-muted">
          Quoting, scheduling and invoicing are in every plan, so they are not here.
        </p>

        <div className="flex flex-col gap-2.5">
          {SELLABLE_FEATURES.map((one) => {
            const row = decided.get(one.key)
            return (
              <div key={one.key} className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <Switch
                    checked={draft[one.key] ?? false}
                    onChange={(value) => setDraft((held) => ({ ...held, [one.key]: value }))}
                    label={one.label}
                  />
                  <p className="m-0 pl-[46px] text-[11px] leading-[1.45] text-text-faint">
                    {one.sub}
                    {one.needs ? ` · needs the ${NEEDS_WORDS[one.needs]} keys` : ''}
                    {row === undefined ? ' · never decided' : ''}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <Field
          label="Why (optional)"
          value={reason}
          onChange={setReason}
          hint="Lands on the audit row beside what changed."
        />

        {error && <p role="alert" className="m-0 text-cell text-danger">{error}</p>}
      </div>
    </Modal>
  )
}

/**
 * Which integration each `needs` names, in words rather than a key.
 *
 * `FeatureDef.needs` is the env-var family (`integration-config.ts`), and a
 * row reading "needs the azure_openai keys" is a row written for whoever
 * wrote the code rather than for whoever is reading the screen.
 */
const NEEDS_WORDS: Record<NonNullable<FeatureDef['needs']>, string> = {
  azure_openai: 'Azure OpenAI',
  qbo: 'QuickBooks',
  stripe: 'Stripe',
  twilio: 'Twilio',
  maps: 'Google Maps',
}

/**
 * The dialog before signing in as somebody — plan §6.12.
 *
 * Read-only is on by default and has to be turned off deliberately, and the
 * reason is a sentence rather than a word: both of those end up in the
 * customer's own banner and their own audit log, so "checking" is not good
 * enough. The customer will read what is typed here.
 */
/**
 * Removing a test client — the confirm (Aaron, step 27).
 *
 * The name typed, not a checkbox: a checkbox is ticked by the hand that is
 * already moving towards the button, and the one thing this dialog exists to
 * prevent is removing the wrong client from a list of several made the same
 * afternoon. What goes is said in full before anything is typed, because
 * "remove" on its own reads like the archive every other screen offers.
 */
export function RemoveClient({ name, kind, people, onClose, onRemove }: {
  name: string
  kind: ClientDetail['tenant']['kind']
  people: number
  onClose: () => void
  onRemove: (confirmName: string, reason: string) => Promise<void>
}) {
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const matches = typed.trim().toLowerCase() === name.trim().toLowerCase()

  return (
    <Modal
      open
      onClose={onClose}
      title={`Remove ${name}?`}
      width={500}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!matches || busy}
            onClick={() => {
              setBusy(true)
              setError(null)
              onRemove(typed, reason.trim())
                .catch((caught: unknown) => setError(
                  caught instanceof ApiError ? caught.message : 'That did not work.'))
                .finally(() => setBusy(false))
            }}
          >
            {busy ? 'Removing…' : 'Remove client'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <p className="m-0 text-cell leading-[1.45] text-text-body">
          {`This ${kind} client goes completely: its account, its `}
          {people === 1 ? 'one person' : `${formatNumber(people)} people`}
          {', and everything they made in it. Anybody whose only account was here loses '
            + 'their password and two-step too, so the same address can be invited fresh. '}
          <b className="font-semibold text-text">It cannot be undone.</b>
        </p>
        <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
          One line stays in the audit log saying who removed it and when. A production
          client is never removed this way.
        </p>
        <Field
          label={`Type ${name} to confirm`}
          value={typed}
          onChange={setTyped}
          autoFocus
        />
        <Field
          label="Why (optional)"
          value={reason}
          onChange={setReason}
          placeholder="Made to test the invite"
        />
        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}

function SignInAs({
  tenantId, user, onClose,
}: {
  tenantId: string
  user: { id: string; name: string }
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [readOnly, setReadOnly] = useState(true)
  const [minutes, setMinutes] = useState(60)
  const [code, setCode] = useState('')
  const [needsCode, setNeedsCode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function go() {
    setBusy(true)
    setError(null)
    try {
      if (needsCode) {
        await adminRequest('/auth/step-up', { method: 'POST', body: { code } })
        setNeedsCode(false)
      }
      await adminRequest('/clients/' + tenantId + '/sign-in-as', {
        method: 'POST',
        body: { userId: user.id, reason, readOnly, minutes },
      })
      /*
       * A full page load, not a route change: the browser is now carrying a
       * tenant cookie as well as the console one, and the office app has to
       * boot as that user from scratch.
       */
      window.location.assign('/')
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'step_up_required') {
        setNeedsCode(true)
        setError('Confirm your code to continue.')
      } else {
        setError(caught instanceof ApiError ? caught.message : 'That did not work.')
      }
      setBusy(false)
    }
  }

  return (
    <Modal open title={`Sign in as ${user.name}`} onClose={onClose} width={440}>
      <div className="flex flex-col gap-3">
        <Field
          label="Why"
          value={reason}
          onChange={setReason}
          autoFocus
          hint="They will see this in their own banner and audit log."
        />

        <Switch
          checked={!readOnly}
          onChange={(value) => setReadOnly(!value)}
          label="Let me make changes"
        />

        <label className="flex items-center gap-2 text-cell text-text">
          <span className="text-sub text-text-muted">Ends after</span>
          <select
            value={minutes}
            onChange={(event) => setMinutes(Number(event.target.value))}
            className="h-8 rounded-field border border-line bg-surface px-2 text-cell"
          >
            <option value={15}>15 minutes</option>
            <option value={60}>1 hour</option>
            <option value={240}>4 hours</option>
          </select>
        </label>

        {needsCode && (
          <Field label="Six-digit code" value={code} onChange={setCode} autoFocus />
        )}

        {error && (
          <p role="alert" className="m-0 text-cell text-danger">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || reason.trim().length < 10}
            onClick={() => void go()}
          >
            {busy ? 'Opening…' : 'Sign in as them'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
